/**
 * Key detection using chroma features + Krumhansl-Schmuckler templates.
 * Adapted for browser-side use with FFT.
 */
import { fft } from './fft';

const NOTES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const MAJOR_PROFILE = [6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88];
const MINOR_PROFILE = [6.33, 2.68, 3.52, 5.38, 2.60, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17];

const FFT_SIZE = 8192;
const HOP_SIZE = 2048;

function hannWindow(size: number): Float64Array {
  const w = new Float64Array(size);
  for (let i = 0; i < size; i++) {
    w[i] = 0.5 * (1 - Math.cos((2 * Math.PI * i) / (size - 1)));
  }
  return w;
}

function computeChroma(data: Float32Array, sampleRate: number): Float64Array {
  const window = hannWindow(FFT_SIZE);
  const numFrames = Math.floor((data.length - FFT_SIZE) / HOP_SIZE) + 1;
  const chromaSum = new Float64Array(12);

  for (let f = 0; f < numFrames; f++) {
    const start = f * HOP_SIZE;
    const frame = new Float64Array(FFT_SIZE);
    for (let i = 0; i < FFT_SIZE; i++) {
      frame[i] = data[start + i] * window[i];
    }
    const { real, imag } = fft(frame);

    const frameChroma = new Float64Array(12);
    for (let k = 1; k < FFT_SIZE / 2; k++) {
      const freq = (k * sampleRate) / FFT_SIZE;
      if (freq <= 0) continue;
      const pitchClass = (Math.round(12 * Math.log2(freq / 440) + 69) % 12 + 12) % 12;
      const mag = real[k] * real[k] + imag[k] * imag[k];
      frameChroma[pitchClass] += mag;
    }

    // Normalize each frame before accumulating
    let max = 0;
    for (let i = 0; i < 12; i++) if (frameChroma[i] > max) max = frameChroma[i];
    if (max > 0) {
      for (let i = 0; i < 12; i++) chromaSum[i] += frameChroma[i] / max;
    }
  }

  // Normalize overall chroma
  let max = 0;
  for (let i = 0; i < 12; i++) if (chromaSum[i] > max) max = chromaSum[i];
  if (max > 0) {
    for (let i = 0; i < 12; i++) chromaSum[i] /= max;
  }
  return chromaSum;
}

function correlation(a: number[], b: number[]): number {
  const n = a.length;
  let sumA = 0, sumB = 0;
  for (let i = 0; i < n; i++) {
    sumA += a[i];
    sumB += b[i];
  }
  const meanA = sumA / n;
  const meanB = sumB / n;
  let num = 0;
  let denA = 0;
  let denB = 0;
  for (let i = 0; i < n; i++) {
    const da = a[i] - meanA;
    const db = b[i] - meanB;
    num += da * db;
    denA += da * da;
    denB += db * db;
  }
  const denom = Math.sqrt(denA * denB);
  return denom === 0 ? 0 : num / denom;
}

export function detectKey(mono: Float32Array, sampleRate: number): { key: string; confidence: number } {
  const chroma = computeChroma(mono, sampleRate);
  const chromaArray = Array.from(chroma);

  let bestKey = 'C major';
  let bestScore = -Infinity;

  for (let s = 0; s < 12; s++) {
    const shifted = [...chromaArray.slice(s), ...chromaArray.slice(0, s)];
    const majorScore = correlation(shifted, MAJOR_PROFILE);
    const minorScore = correlation(shifted, MINOR_PROFILE);
    if (majorScore > bestScore) {
      bestScore = majorScore;
      bestKey = `${NOTES[s]} major`;
    }
    if (minorScore > bestScore) {
      bestScore = minorScore;
      bestKey = `${NOTES[s]} minor`;
    }
  }

  const confidence = Math.min(1, Math.max(0, (bestScore + 1) / 2));
  return { key: bestKey, confidence: Math.round(confidence * 100) / 100 };
}
